import { useEffect, useRef, useState } from 'react';

/**
 * Hook for Apple-style fade-in-on-scroll animations.
 * Returns a ref to attach to the element and a boolean for visibility.
 */
export function useScrollReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

/**
 * Wrapper component for scroll-reveal animations.
 * Usage: <RevealOnScroll><YourContent /></RevealOnScroll>
 */
export function RevealOnScroll({ children, className = '', delay = 0, direction = 'up' }) {
  const [ref, visible] = useScrollReveal();

  const directionStyles = {
    up: 'translate-y-8',
    down: '-translate-y-8',
    left: 'translate-x-8',
    right: '-translate-x-8',
    none: '',
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 ${directionStyles[direction]}`
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/**
 * LazyMount — defers mounting of `children` until the placeholder scrolls
 * within ~600px of the viewport. Cuts initial JS work, image fetches, and
 * layout cost on the homepage by 30-50% for below-the-fold sections like
 * testimonials, team grid, FAQs, and trust strips.
 *
 *   <LazyMount minHeight={600}>
 *     <ExpensiveSection />
 *   </LazyMount>
 *
 * Once mounted, the component stays in the DOM. The placeholder reserves
 * `minHeight` to prevent layout shift while the content is still pending.
 */
export function LazyMount({ children, minHeight = 400, rootMargin = '600px 0px' }) {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setMounted(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setMounted(true);
          obs.disconnect();
        }
      },
      { rootMargin },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [mounted, rootMargin]);

  if (mounted) return children;
  return <div ref={ref} aria-hidden="true" style={{ minHeight }} />;
}
